import { useRef, useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { Upload, FileText, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'

const BUCKET = 'journalist-passports'

export function PassportUploadCard({ journalistId }: { journalistId: string }) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [expiry, setExpiry] = useState('')
  const [country, setCountry] = useState('')
  const [signedUrl, setSignedUrl] = useState<string | null>(null)

  const { data: accr, isLoading } = useQuery({
    queryKey: ['my-passport-record', journalistId],
    enabled: !!journalistId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('journalist_accreditations' as any)
        .select('id, passport_url, passport_expiry, passport_country, passport_status, passport_notified')
        .eq('journalist_id', journalistId)
        .order('accredited_at', { ascending: false })
        .limit(1)
        .maybeSingle() as any)
      return data as any
    },
  })

  useEffect(() => {
    if (!accr?.passport_url) { setSignedUrl(null); return }
    supabase.storage.from(BUCKET)
      .createSignedUrl(accr.passport_url, 3600)
      .then(({ data }) => setSignedUrl(data?.signedUrl ?? null))
  }, [accr?.passport_url])

  const passportStatus = accr?.passport_status ?? 'none'

  const statusConfig: Record<string, { label: string; cls: string; icon: any }> = {
    none:          { label: 'No passport uploaded',  cls: 'text-muted-foreground', icon: null },
    valid:         { label: 'Valid',                  cls: 'text-emerald-700',      icon: CheckCircle2 },
    expiring_soon: { label: 'Expiring soon',          cls: 'text-warning',        icon: AlertTriangle },
    expired:       { label: 'Expired',                cls: 'text-destructive',          icon: AlertTriangle },
    pending_upload:{ label: 'Renewal requested',      cls: 'text-violet-700',       icon: AlertTriangle },
  }

  const sc = statusConfig[passportStatus] ?? statusConfig.none
  const StatusIcon = sc.icon

  async function handleUpload() {
    const file = fileRef.current?.files?.[0]
    if (!file || !expiry || !country) {
      toast({ title: 'Please select a file and fill in expiry and country.', variant: 'destructive' })
      return
    }
    setUploading(true)
    try {
      const ext  = file.name.split('.').pop()
      const path = `${journalistId}/passport_${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: true })
      if (upErr) throw upErr

      // Store the storage path — signed URLs are generated at display time
      if (accr?.id) {
        await (supabase
          .from('journalist_accreditations' as any)
          .update({
            passport_url:     path,
            passport_expiry:  expiry,
            passport_country: country,
            passport_status:  'valid',
            passport_notified: false,
          })
          .eq('id', accr.id) as any)
      }

      toast({ title: 'Passport uploaded successfully.' })
      qc.invalidateQueries({ queryKey: ['my-passport-record', journalistId] })
      if (fileRef.current) fileRef.current.value = ''
    } catch (e: any) {
      toast({ title: 'Upload failed', description: e.message, variant: 'destructive' })
    } finally {
      setUploading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Passport / Travel Document
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Current status */}
            <div className={`flex items-center gap-2 text-sm font-medium ${sc.cls}`}>
              {StatusIcon && <StatusIcon className="w-4 h-4" />}
              {sc.label}
              {accr?.passport_expiry && (
                <span className="font-normal text-muted-foreground">
                  · Expires {new Date(accr.passport_expiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              )}
            </div>

            {(passportStatus === 'expired' || passportStatus === 'pending_upload') && (
              <div className="bg-[hsl(var(--destructive)/0.08)] border border-[hsl(var(--destructive)/0.3)] rounded-lg p-3 text-xs text-destructive">
                Your passport has expired or renewal has been requested. Please upload a new, valid document.
              </div>
            )}

            {passportStatus === 'expiring_soon' && (
              <div className="bg-[hsl(var(--warning)/0.08)] border border-[hsl(var(--warning)/0.3)] rounded-lg p-3 text-xs text-warning">
                Your passport expires soon. Consider uploading a renewed document before it expires.
              </div>
            )}

            {/* Current file link — signed URL (private bucket) */}
            {accr?.passport_url && signedUrl && (
              <a
                href={signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[12px] text-accent hover:underline flex items-center gap-1"
              >
                <FileText className="w-3 h-3" /> View current document
              </a>
            )}

            {/* Upload form */}
            <div className="space-y-3 pt-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Passport Expiry</Label>
                  <Input
                    type="date"
                    value={expiry}
                    onChange={e => setExpiry(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Country of Issue</Label>
                  <Input
                    placeholder="e.g. Nigeria"
                    value={country}
                    onChange={e => setCountry(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Upload Document (PDF, JPG, PNG — max 10 MB)</Label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="block w-full text-xs text-muted-foreground
                    file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border
                    file:border-border file:bg-muted file:text-xs file:font-medium
                    file:cursor-pointer hover:file:bg-muted/80"
                />
              </div>

              <Button onClick={handleUpload} disabled={uploading} size="sm">
                {uploading
                  ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading…</>
                  : <><Upload className="w-4 h-4 mr-1.5" /> Upload Passport</>
                }
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
